import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import classNames from 'classnames';
import { connect } from 'react-redux';
import {
  change,
  stopSubmit,
  Field,
  FieldArray,
  reduxForm,
  untouch,
  updateSyncErrors
} from 'redux-form';
import { useQuery } from '@apollo/react-hooks';

import Dialog from '@material-ui/core/Dialog';
import { Typography } from '@material-ui/core';

import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';

import {
  ACTIVITIES_AVAILABLE_PARTICIPANTS,
  ACTIVITY
} from 'graphql/queries/activities';

import ConfirmContext from 'contexts/ConfirmContext';

import {
  BookInfoList,
  CircularLoader,
  MultiDatePickerCalendar,
  RadioButtonsGroup,
  SingleDatePicker
} from 'components';

import Header from 'pages/widget/components/header/Header';
import Footer from 'pages/widget/components/footer/Footer';
import Visitors from 'pages/widget/components/visiters/Visiters';

import {
  getReceivedActivityBookingsDates,
  getReceivedActivityData
} from 'utils/accommodations-and-activities/prepareReceivedActivityData';
import {
  getAvailableRangeDates,
  getDefaultStartTime,
  getDisabledDates,
  getListOfStartTimeAndDuration,
  getListOfStartTimeAndDurationByCutoffPeriod
} from 'utils/book-activity/availableDates';
import {
  getAvailableParticipantsForStartTime,
  getMaxParticipants,
  getMaxParticipantsActivitySeveralDays
} from 'utils/book-activity/maxParticipants';
import { getParticipants } from 'utils/book-activity/prepareParticipants';
import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import AddOnsList from 'pages/book-activity/components/add-ons-list/AddOnsList';

import validate from './validate';

import { useStyles } from '../styles';

const SCHEDULE_DATE_NO_TIME_NAME = 'scheduleDateForm';
const SCHEDULE_DATE_AND_TIME_NAME = 'scheduleDateTimeForm';
const SCHEDULE_PASS_NAME = 'schedulePassForm';
const ACTIVITY_FUTURE_PRICES = 'activityFuturePricesForm';

const ActivityDialog = ({
  open,
  accommodationData,
  closeDialogCallback,
  initialValues,
  widgetForm,
  changeFormFieldValue,
  stopSubmitForm,
  untouchFormFields,
  handleSubmit,
  ownerId,
  updateFormErrors,
  logo
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const confirm = React.useContext(ConfirmContext);

  const currency = widgetForm.values.currency;
  const activityIndex = initialValues.index;
  const reduxActivity = widgetForm.values.activities[activityIndex];

  const [activity, setActivity] = React.useState(null);
  const [
    listStartTimeAndDuration,
    setListStartTimeAndDuration
  ] = React.useState([]);
  const [isUpdate, setIsUpdate] = React.useState(false);
  const [
    availableParticipantsData,
    setAvailableParticipantsData
  ] = React.useState(null);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const [isSelected, setIsSelected] = React.useState(false);

  const initialStartingDate =
    initialValues.startingDate || moment() || initialValues.multiDates;

  React.useLayoutEffect(() => {
    const updateSize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const { loading, data } = useQuery(ACTIVITY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      activityId: initialValues.id,
      businessOwnerId: ownerId
    }
  });

  const {
    data: receivedAvailableParticipantsData,
    refetch: refetchParticipantsData
  } = useQuery(ACTIVITIES_AVAILABLE_PARTICIPANTS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      businessOwnerId: ownerId,
      activityId: initialValues.id,
      startPeriod: moment(initialStartingDate)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(initialStartingDate)
        .endOf('month')
        .format('YYYY-MM-DD')
    }
  });

  React.useEffect(() => {
    setIsSelected(reduxActivity.isSelected);
  }, []);

  React.useEffect(() => {
    if (!receivedAvailableParticipantsData) return;

    const availableParticipantsData = getReceivedActivityBookingsDates(
      receivedAvailableParticipantsData
    );

    setAvailableParticipantsData(availableParticipantsData);
  }, [receivedAvailableParticipantsData]);

  React.useEffect(() => {
    if (reduxActivity.startTime) {
      changeFormFieldValue(
        `activities[${activityIndex}].maxParticipants`,
        reduxActivity.startTime.seatsLeft
      );
    }
  }, [reduxActivity.startTime]);

  React.useEffect(() => {
    if (!data || !data.activities.length) return;

    const receivedActivity = data.activities[0];
    const activityData = getReceivedActivityData(receivedActivity);

    if (receivedActivity?.futurePrices?.length > 0) {
      const hasValidFuturePrices = receivedActivity.futurePrices.some(
        fp => fp.ratePlanPrices && fp.ratePlanPrices.length > 0
      );
      
      if (hasValidFuturePrices) {
        activityData['futurePriceForm'] = receivedActivity['futurePriceForm'];
        
        activityData['pricesType'] = receivedActivity['pricesType']; 
       
      }
    } else {

      activityData['pricesType'] = receivedActivity['pricesType'];
    }
    activityData['pricesType'] = receivedActivity['pricesType'];
    activityData['scheduleType'] = receivedActivity['scheduleType'];

    setMaxParticipants(activityData['generalForm'].maxParticipants);

    if (activityData.scheduleType === SCHEDULE_PASS_NAME) {
      setParticipants(activityData);
    }

    if (initialValues.isSelected) {
      setParticipants(activityData);
      handleListStartTimeAndDurationField(activityData);
    }

    changeFormFieldValue(`activities[${activityIndex}].isSelected`, true);

    setAddOns(activityData);

    setActivity(activityData);
  }, [data]);

  const setAddOns = activityData => {
    if (!reduxActivity.addOns || !reduxActivity.addOns.length) {
      const addOnsList = activityData.addOnsForm.addOns.map(item => {
        return {
          ...item,
          value: 0
        };
      });
      changeFormFieldValue(`activities[${activityIndex}].addOns`, addOnsList);
    }
  };

  React.useEffect(() => {
    if (!reduxActivity.startingDate) return;
    setParticipants(activity);
    handleListStartTimeAndDurationField(activity);
  }, [reduxActivity.startingDate]);

  React.useEffect(() => {
    const multiDates = reduxActivity.multiDates;
    if (multiDates) {
      handleMultiDateAvailableSeats(activity);
      setParticipants(activity);
    }
  }, [reduxActivity.multiDates]);

  const setParticipants = activity => {
    if (!activity) return;
    const previousParticipants = reduxActivity.participants || [];
    const participants = getParticipants(
      activity,
      { values: reduxActivity },
      () => {},
      false,
      currency,
      previousParticipants
    );

    changeFormFieldValue(
      `activities[${activityIndex}].participants`,
      participants
    );

    setIsUpdate(!isUpdate);
  };

  const changeMonthHandler = date => {
    refetchParticipantsData({
      businessOwnerId: ownerId,
      activityId: initialValues.id,
      startPeriod: moment(date)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(date)
        .endOf('month')
        .format('YYYY-MM-DD')
    });
  };

  const handleMultiDateAvailableSeats = activity => {
    if (!activity || !reduxActivity.multiDates) return;

    const startDate = reduxActivity.multiDates.startDate;
    const endDate = reduxActivity.multiDates.endDate;

    if (!startDate || !endDate) return;

    const availableParticipantsPerDate = availableParticipantsData
      ? availableParticipantsData.activities[0].availableParticipantsPerDate
      : [];

    const availableParticipants = getMaxParticipantsActivitySeveralDays(
      availableParticipantsPerDate,
      startDate,
      endDate
    );

    if (availableParticipants !== null) {
      setMaxParticipants(availableParticipants);
    } else {
      setMaxParticipants(activity.generalForm.maxParticipants);
    }
  };

  const handleListStartTimeAndDurationField = activity => {
    if (!activity) return;

    const startingDate = reduxActivity.startingDate;
    const availableParticipantsPerDate = availableParticipantsData
      ? availableParticipantsData.activities[0].availableParticipantsPerDate
      : [];
    let listOfStartTimeAndDuration = getListOfStartTimeAndDuration(
      availableParticipantsPerDate,
      activity,
      { values: reduxActivity }
    );
    listOfStartTimeAndDuration = getListOfStartTimeAndDurationByCutoffPeriod(
      activity,
      listOfStartTimeAndDuration,
      startingDate
    );
    let selectedStartTime = getDefaultStartTime(listOfStartTimeAndDuration, {
      values: reduxActivity
    });

    if (!selectedStartTime) {
      setMaxParticipants(activity.generalForm.maxParticipants);
      availableParticipantsPerDate.map(item => {
        const formattedStartDate = startingDate
          ? moment(startingDate).format('YYYY-MM-DD')
          : '';
        if (!item.startTime && item.date === formattedStartDate) {
          setMaxParticipants(item.availableParticipants);
        }
      });
    } else {
      setMaxParticipants(selectedStartTime.seatsLeft);

      listOfStartTimeAndDuration = getAvailableParticipantsForStartTime(
        listOfStartTimeAndDuration,
        null,
        { values: reduxActivity },
        availableParticipantsData
      );
    }

    selectedStartTime = getDefaultStartTime(listOfStartTimeAndDuration, {
      values: reduxActivity
    });

    changeFormFieldValue(
      `activities[${activityIndex}].startTime`,
      selectedStartTime
    );

    // listOfStartTimeAndDuration = listOfStartTimeAndDuration.map(item => {
    //   return {
    //     ...item,
    //     title: `${item.startTime} • ${item.duration} ${item.timeType}`
    //   };
    // });

    setListStartTimeAndDuration(listOfStartTimeAndDuration);
  };

  const setMaxParticipants = seatsLeft => {
    const maxParticipants = getMaxParticipants(
      seatsLeft,
      null,
      { values: reduxActivity },
      availableParticipantsData
    );

    changeFormFieldValue(
      `activities[${activityIndex}].maxParticipants`,
      maxParticipants
    );
  };

  const cancelActivity = isRemove => {
    const title = isRemove
      ? 'Are you sure you want to remove it?'
      : 'Are you sure you want to cancel it?';
    const confirmationText = isRemove ? 'Remove' : 'Cancel';
    confirm({
      title: title,
      confirmationText: confirmationText,
      cancellationText: 'Back'
    })
      .then(() => {
        changeFormFieldValue(`activities[${activityIndex}].startingDate`, null);
        changeFormFieldValue(`activities[${activityIndex}].multiDates`, null);
        changeFormFieldValue(`activities[${activityIndex}].participants`, []);
        changeFormFieldValue(`activities[${activityIndex}].startTime`, null);

        changeFormFieldValue(`activities[${activityIndex}].isSelected`, false);

        untouchFormFields([`activities[${activityIndex}].startingDate`]);
        untouchFormFields([`activities[${activityIndex}].multiDates`]);
        updateFormErrors();
        stopSubmitForm();

        closeDialogCallback();
      })
      .catch(() => {});
  };

  const saveActivity = () => {
    if (widgetForm.syncErrors) return;

    changeFormFieldValue(`activities[${activityIndex}].isSelected`, true);
    closeDialogCallback();
  };

  const getStartTimeAndDuration = () => {
    return listStartTimeAndDuration
      .map(item => {
        return {
          ...item,
          title: `${item.startTime} • ${item.duration} ${t(item.timeType)}`
        };
      })
      .filter(item => item.seatsLeft);
  };

  const currentLanguageGeneralForm = activity
    ? getFormByCurrentLanguage(
        i18n.language,
        activity.generalForm,
        activity['generalForm-Norwegian']
      )
    : {};

  return (
    <Dialog open={open} classes={{ paper: classes.paper }}>
      <form onSubmit={handleSubmit(saveActivity)} className={classes.form}>
        <CircularLoader
          isLoading={loading}
          loaderClassName={classes.loaderClassName}
        />

        <Header
          logo={logo}
          cancelCallback={cancelActivity}
          title={t('Book your activity')}
        />

        {activity && (
          <div className={classes.content}>
            <Typography variant="h2" classes={{ root: classes.title }}>
              {currentLanguageGeneralForm.title}
            </Typography>
            <div className={classes.location}>
              <LocationOnOutlinedIcon className={classes.locationIcon} />
              <Typography variant="body1" className={classes.locationText}>
                {activity.generalForm.startingPoint}
              </Typography>
            </div>
            <div className={classes.editSection}>
              {activity.scheduleType === SCHEDULE_DATE_NO_TIME_NAME && (
                <div className={classNames(classes.date, classes.item)}>
                  <Field
                    name={`activities[${activityIndex}].multiDates`}
                    label="Select date"
                    initialFocus="startDate"
                    dateRangePickerClass="dateRangeOpenedPicker"
                    inputClassName={classes.disableInputFocus}
                    availableRange={getAvailableRangeDates(activity)}
                    disabledDates={getDisabledDates(
                      availableParticipantsData,
                      null,
                      activity
                    )}
                    changeMonthsCallback={changeMonthHandler}
                    numberOfMonths={
                      windowWidth > 1010 || windowWidth <= 750 ? 1 : 2
                    }
                    daySize={windowWidth > 600 ? 38 : 41}
                    labelTypographyVariant="h3"
                    component={MultiDatePickerCalendar}
                    labelClass={classes.label}
                    cutoffData={activity[activity.scheduleType]}
                  />
                </div>
              )}
              {activity.scheduleType === SCHEDULE_DATE_AND_TIME_NAME && (
                <div className={classNames(classes.date, classes.item)}>
                  <Field
                    name={`activities[${activityIndex}].startingDate`}
                    label="Select date"
                    availableRange={getAvailableRangeDates(activity)}
                    disabledDates={getDisabledDates(
                      availableParticipantsData,
                      null,
                      activity
                    )}
                    changeMonthsCallback={changeMonthHandler}
                    numberOfMonths={
                      windowWidth > 1010 || windowWidth <= 750 ? 1 : 2
                    }
                    daySize={windowWidth > 600 ? 38 : 41}
                    labelTypographyVariant="h3"
                    component={SingleDatePicker}
                    labelClass={classes.label}
                    cutoffData={activity[activity.scheduleType]}
                  />
                </div>
              )}
              {!!reduxActivity.participants && (
                <div className={classes.item}>
                  <FieldArray
                    name={`activities[${activityIndex}].participants`}
                    title="Guests"
                    isUpdate={isUpdate}
                    currency={currency}
                    wrapperClass={classes.visitorsWrapperClass}
                    itemClass={classes.visitItem}
                    titleClass={classes.label}
                    component={Visitors}
                  />
                </div>
              )}
              {!!listStartTimeAndDuration.length && (
                <div className={classes.item}>
                  <Typography
                    variant="h3"
                    className={classes.availabilityTitle}
                  >
                    {t('Start time & Duration')}
                  </Typography>
                  <Field
                    name={`activities[${activityIndex}].startTime`}
                    list={getStartTimeAndDuration()}
                    component={RadioButtonsGroup}
                    labelClass={classes.availabilityLabel}
                    isId={true}
                    checkboxTitleField="title"
                    radioGroupRootClass={classes.radioGroupRoot}
                  />
                </div>
              )}
            </div>
            <div>
              {reduxActivity.addOns && !!reduxActivity.addOns.length && (
                <Typography
                  variant="h3"
                  classes={{ root: classes.sectionTitleIndent }}
                >
                  {t('Add-ons')}
                </Typography>
              )}
              <FieldArray
                name={`activities[${activityIndex}].addOns`}
                currency={currency}
                wrapperClass={classes.addOnWrapperClass}
                addOnItemClass={classes.addOnItemClass}
                addOnDescClass={classes.addOnDescClass}
                isWidget={true}
                component={AddOnsList}
              />
            </div>
            <BookInfoList
              activity={activity}
              currency={currency}
              defaultExpanded={false}
              firstDefaultExpanded={true}
              isOverview={true}
              isImage={true}
            />
          </div>
        )}

        <Footer
          isHiddenNavigation={true}
          isAccommodationBooking={true}
          isSelectedProduct={false}
          cancelButton={isSelected ? 'Remove Activity' : 'Cancel Activity'}
          confirmButton={isSelected ? 'Save Activity' : 'Add Activity'}
          cancelCallback={() => cancelActivity(isSelected)}
          accommodationData={accommodationData}
        />
      </form>
    </Dialog>
  );
};

const mapStateToProps = state => {
  return {
    widgetForm: state.form.widgetForm
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('widgetForm', field, value));
  },
  stopSubmitForm: () => {
    dispatch(stopSubmit('widgetForm', {}));
  },
  untouchFormFields: fields => {
    dispatch(untouch('widgetForm', ...fields));
  },
  updateFormErrors: () => dispatch(updateSyncErrors('widgetForm', {}))
});

export default reduxForm({
  form: 'widgetForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnBlur: false,
  validate
})(connect(mapStateToProps, mapDispatchToProps)(ActivityDialog));
